/**
 * Meetings (F1): organizer, guests, public/private, RECURRENCE
 * (RRULE subset with the church's explicit timezone) and specific
 * location within the venue. The list shows what the `meetings`
 * grants sync; creating requires the scope's `meetings:write`.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  buildRRule,
  getChurchesQuery,
  getGroupsQuery,
  getMeetingsQuery,
  getPeopleQuery,
  getVenuesQuery,
  parseInstant,
  queryKeys,
  RRULE_WEEKDAYS,
  type RRuleFreq,
  type RRuleWeekday,
} from "@agape/shared";
import {
  type ChurchGroupRecord,
  type ChurchRecord,
  type MeetingRecord,
  meeting as meetingTable,
  type PersonRecord,
  type VenueRecord,
} from "@agape/powersync";
import { CalendarClock, Plus, Repeat } from "@fleet/icons";

import { Link } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Select } from "@/components/ui/select";
import { Switch } from "@fleet/ui/switch";
import { showToast } from "@/components/ui/toast";
import { Field, FormDialog } from "./form";
import {
  formatWhen,
  localInputToIso,
  newId,
  nowIso,
  useDomainQuery,
  useGrants,
  useWriter,
} from "../use-domain";

export const MEETING_KINDS = ["service", "cell", "prayer", "study", "special", "other"] as const;

type Translate = ReturnType<typeof useI18n>["t"];

export function meetingKindLabel(t: Translate, kind: string | null): string {
  switch (kind) {
    case "service": {
      return t(msg`Service`);
    }
    case "cell": {
      return t(msg`Cell`);
    }
    case "prayer": {
      return t(msg`Prayer`);
    }
    case "study": {
      return t(msg`Study`);
    }
    case "special": {
      return t(msg`Special`);
    }
    default: {
      return t(msg`Other`);
    }
  }
}

export function MeetingsPage() {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();

  const meetings = useDomainQuery<MeetingRecord>(
    (uid) => queryKeys.domain.meetings(uid),
    (db) => getMeetingsQuery(db),
  );
  const churches = useDomainQuery<ChurchRecord>(
    (uid) => queryKeys.domain.churches(uid),
    (db) => getChurchesQuery(db),
  );
  const venues = useDomainQuery<VenueRecord>(
    (uid) => queryKeys.domain.venues(uid),
    (db) => getVenuesQuery(db),
  );
  const groups = useDomainQuery<ChurchGroupRecord>(
    (uid) => queryKeys.domain.groups(uid),
    (db) => getGroupsQuery(db),
  );
  const people = useDomainQuery<PersonRecord>(
    (uid) => queryKeys.domain.people(uid),
    (db) => getPeopleQuery(db),
  );

  const churchName = (id: string | null) =>
    (churches.data ?? []).find((c) => c.id === id)?.name ?? "";
  const groupNameOf = (id: string | null) =>
    (groups.data ?? []).find((g) => g.id === id)?.name ?? "";

  // Churches/groups where meetings can be created.
  const writableChurches = createMemo(() =>
    (churches.data ?? []).filter((c) => grants.can([c.id], "meetings", "write")),
  );
  const writableGroups = createMemo(() =>
    (groups.data ?? []).filter((g) => grants.can([g.id, g.church_id], "meetings", "write")),
  );

  // ── Create meeting ────────────────────────────────────────────────────────
  const [open, setOpen] = createSignal(false);
  const [title, setTitle] = createSignal("");
  const [description, setDescription] = createSignal("");
  const [churchId, setChurchId] = createSignal("");
  const [groupId, setGroupId] = createSignal("");
  const [venueId, setVenueId] = createSignal("");
  const [organizerId, setOrganizerId] = createSignal("");
  const [kind, setKind] = createSignal<string>("service");
  const [isPublic, setIsPublic] = createSignal(false);
  const [startsAt, setStartsAt] = createSignal("");
  const [endsAt, setEndsAt] = createSignal("");
  const [locationDetail, setLocationDetail] = createSignal("");
  // Recurrence
  const [freq, setFreq] = createSignal<"none" | RRuleFreq>("none");
  const [interval, setIntervalValue] = createSignal("1");
  const [byDay, setByDay] = createSignal<RRuleWeekday[]>([]);
  const [count, setCount] = createSignal("");

  const effectiveChurch = () => churchId() || writableChurches()[0]?.id || "";
  const churchVenues = createMemo(() =>
    (venues.data ?? []).filter((v) => v.church_id === effectiveChurch()),
  );
  const churchGroups = createMemo(() =>
    writableGroups().filter((g) => g.church_id === effectiveChurch()),
  );
  const churchPeople = createMemo(() =>
    (people.data ?? []).filter((p) => p.church_id === effectiveChurch()),
  );

  const rrule = createMemo(() => {
    const f = freq();
    if (f === "none") {
      return null;
    }
    return buildRRule({
      freq: f,
      interval: Math.max(1, Number(interval()) || 1),
      byDay: f === "WEEKLY" && byDay().length > 0 ? byDay() : undefined,
      count: count() ? Number(count()) : undefined,
    });
  });

  const create = async () => {
    const starts = localInputToIso(startsAt());
    const church = effectiveChurch();
    if (!title().trim() || !starts || !church) {
      return;
    }
    const tz = (churches.data ?? []).find((c) => c.id === church)?.timezone ?? "Europe/Madrid";
    await write()
      .insert(meetingTable)
      .values({
        id: newId(),
        church_id: church,
        group_id: groupId() || null,
        venue_id: venueId() || null,
        organizer_person_id: organizerId() || null,
        title: title().trim(),
        description: description().trim() || null,
        kind: kind(),
        is_public: isPublic() ? 1 : 0,
        starts_at: starts,
        ends_at: localInputToIso(endsAt()),
        timezone: tz,
        rrule: rrule(),
        location_detail: locationDetail().trim() || null,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setOpen(false);
    setTitle("");
    setDescription("");
    setStartsAt("");
    setEndsAt("");
    setLocationDetail("");
    setFreq("none");
    setByDay([]);
    setCount("");
    showToast(t(msg`Meeting created`), "success");
  };

  const toggleDay = (day: RRuleWeekday) => {
    setByDay((days) => (days.includes(day) ? days.filter((d) => d !== day) : [...days, day]));
  };

  return (
    <div class="space-y-4">
      <PageSubHeader title={t(msg`Meetings`)} />
      <div class="flex items-center justify-between gap-3">
        <p class="text-sm text-muted-foreground">
          {t(msg`Meetings your account can see — church services and group gatherings.`)}
        </p>
        <Show when={writableChurches().length > 0 || writableGroups().length > 0}>
          <Button size="sm" data-testid="meeting-create" onClick={() => setOpen(true)}>
            <Plus size={16} /> {t(msg`New meeting`)}
          </Button>
        </Show>
      </div>
      <Show
        when={(meetings.data ?? []).length > 0}
        fallback={
          <p data-testid="meetings-empty" class="text-muted-foreground">
            {t(msg`No meetings synced to this account yet.`)}
          </p>
        }
      >
        <div class="space-y-2" data-testid="meetings-list">
          <For each={meetings.data ?? []}>
            {(m) => (
              <Link to="/meetings/$meetingId" params={{ meetingId: m.id }} class="block">
                <Card
                  data-testid="meeting-item"
                  data-meeting-id={m.id}
                  class="transition-colors hover:border-primary/40"
                >
                  <CardContent class="flex items-center gap-3 py-3">
                    <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                      <CalendarClock size={16} />
                    </span>
                    <div class="min-w-0 flex-1">
                      <p class="truncate font-medium">{m.title}</p>
                      <p class="truncate text-sm text-muted-foreground">
                        {[
                          meetingKindLabel(t, m.kind),
                          churchName(m.church_id),
                          groupNameOf(m.group_id),
                          m.is_public ? t(msg`Public`) : t(msg`Private`),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </div>
                    <div class="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                      <Show when={m.rrule}>
                        <Repeat size={14} aria-label={t(msg`Recurring`)} />
                      </Show>
                      <span>{m.starts_at ? formatWhen(parseInstant(m.starts_at) ?? 0) : ""}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )}
          </For>
        </div>
      </Show>

      <FormDialog
        open={open()}
        onOpenChange={setOpen}
        title={t(msg`New meeting`)}
        icon={<CalendarClock size={20} />}
        submitTestId="meeting-submit"
        disabled={!title().trim() || !startsAt()}
        onSubmit={create}
      >
        <Field
          id="m-title"
          label={t(msg`Title`)}
          value={title()}
          onInput={setTitle}
          testId="meeting-title-input"
        />
        <Field
          id="m-description"
          label={t(msg`Description`)}
          value={description()}
          onInput={setDescription}
        />
        <div class="grid grid-cols-2 gap-3">
          <Field id="m-church" label={t(msg`Church`)}>
            <Select
              options={writableChurches().map((c) => ({ value: c.id, label: c.name ?? c.id }))}
              value={effectiveChurch()}
              onChange={(v) => {
                setChurchId(v);
                setGroupId("");
                setVenueId("");
                setOrganizerId("");
              }}
              aria-label={t(msg`Church`)}
            />
          </Field>
          <Field id="m-kind" label={t(msg`Kind`)}>
            <Select
              options={MEETING_KINDS.map((k) => ({ value: k, label: meetingKindLabel(t, k) }))}
              value={kind()}
              onChange={setKind}
              aria-label={t(msg`Kind`)}
            />
          </Field>
          <Field id="m-group" label={t(msg`Group (optional)`)}>
            <Select
              options={[
                { value: "", label: t(msg`Whole church`) },
                ...churchGroups().map((g) => ({ value: g.id, label: g.name ?? g.id })),
              ]}
              value={groupId()}
              onChange={setGroupId}
              aria-label={t(msg`Group`)}
            />
          </Field>
          <Field id="m-venue" label={t(msg`Venue (optional)`)}>
            <Select
              options={[
                { value: "", label: t(msg`No venue`) },
                ...churchVenues().map((v) => ({ value: v.id, label: v.name ?? v.id })),
              ]}
              value={venueId()}
              onChange={setVenueId}
              aria-label={t(msg`Venue`)}
            />
          </Field>
        </div>
        <Field
          id="m-location"
          label={t(msg`Specific location within the venue`)}
          value={locationDetail()}
          onInput={setLocationDetail}
          placeholder={t(msg`Main hall, room 2, patio…`)}
          testId="meeting-location-input"
        />
        <Field id="m-organizer" label={t(msg`Organizer`)}>
          <Select
            options={[
              { value: "", label: t(msg`Nobody yet`) },
              ...churchPeople().map((p) => ({ value: p.id, label: p.name ?? p.id })),
            ]}
            value={organizerId()}
            onChange={setOrganizerId}
            aria-label={t(msg`Organizer`)}
          />
        </Field>
        <div class="grid grid-cols-2 gap-3">
          <Field
            id="m-starts"
            label={t(msg`Starts`)}
            type="datetime-local"
            value={startsAt()}
            onInput={setStartsAt}
            testId="meeting-starts-input"
          />
          <Field
            id="m-ends"
            label={t(msg`Ends (optional)`)}
            type="datetime-local"
            value={endsAt()}
            onInput={setEndsAt}
          />
        </div>
        <div class="flex items-center justify-between rounded-md border border-border px-3 py-2">
          <span class="text-sm">{t(msg`Public meeting`)}</span>
          <Switch
            checked={isPublic()}
            onCheckedChange={setIsPublic}
            aria-label={t(msg`Public meeting`)}
            data-testid="meeting-public"
          />
        </div>

        {/* ── Recurrencia ───────────────────────────────────────────────── */}
        <div class="space-y-3 rounded-md border border-border p-3">
          <div class="flex items-center gap-2 text-sm font-medium">
            <Repeat size={14} /> {t(msg`Recurrence`)}
          </div>
          <div class="grid grid-cols-2 gap-3">
            <Field id="m-freq" label={t(msg`Repeats`)}>
              <Select<"none" | RRuleFreq>
                options={[
                  { value: "none", label: t(msg`Does not repeat`) },
                  { value: "DAILY", label: t(msg`Daily`) },
                  { value: "WEEKLY", label: t(msg`Weekly`) },
                  { value: "MONTHLY", label: t(msg`Monthly`) },
                  { value: "YEARLY", label: t(msg`Yearly`) },
                ]}
                value={freq()}
                onChange={setFreq}
                aria-label={t(msg`Repeats`)}
              />
            </Field>
            <Show when={freq() !== "none"}>
              <Field
                id="m-interval"
                label={t(msg`Every N`)}
                type="number"
                value={interval()}
                onInput={setIntervalValue}
              />
            </Show>
          </div>
          <Show when={freq() === "WEEKLY"}>
            <div class="flex flex-wrap gap-1.5" data-testid="meeting-weekdays">
              <For each={[...RRULE_WEEKDAYS]}>
                {(day) => (
                  <button
                    type="button"
                    data-testid={`weekday-${day}`}
                    onClick={() => toggleDay(day)}
                    class={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                      byDay().includes(day)
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border text-muted-foreground"
                    }`}
                  >
                    {day}
                  </button>
                )}
              </For>
            </div>
          </Show>
          <Show when={freq() !== "none"}>
            <Field
              id="m-count"
              label={t(msg`Number of occurrences (optional)`)}
              type="number"
              value={count()}
              onInput={setCount}
            />
            <p class="text-xs text-muted-foreground">
              {t(msg`Times follow the church timezone — wall-clock time is preserved across DST.`)}
            </p>
          </Show>
        </div>
      </FormDialog>
    </div>
  );
}
